import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import Edit from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { RepairHistoryDto } from 'api';
import { PdfIcon } from 'assets/images/documents';
import { Id } from 'domain/Id';
import { AreaName } from 'domain/constants';
import { DataGridToolbar } from 'library/components/controls/datagrid/DataGridToolbar';
import { LinkActionItem } from 'library/components/controls/datagrid/LinkActionItem';
import { VolvoDataGrid } from 'library/components/controls/datagrid/VolvoDataGrid';
import {
	getRepairEditLink,
	getRepairPrintLink,
	getRepairViewLink,
} from 'library/components/link/linkUtils';
import { getLocalizedDateFormat } from 'library/formatters/DateFormatter';
import { toPrecision } from 'library/formatters/NumberFormatter';
import { useDialogState } from 'library/hooks/useDialogState';
import { OperationType } from 'library/models/enums/OperationType';
import { checkUserAccess } from 'main/auth/checkUserAccess';
import { CountryCell } from 'modules/EndCustomer/Shared/DataGrid/CellRender/CountryCell';
import { ProductIndividualRepairDeleteActionItem } from 'modules/ProductIndividualview/Components/ProductIndividualRepairDeleteActionItem';
import { ProductIndividualRepairDeleteDialog } from 'modules/ProductIndividualview/Components/ProductIndividualRepairDeleteDialog';

interface RepairsDataGridProps {
	data: RepairHistoryDto[];
	showSerialNumber?: boolean;
}

export const RepairsDataGrid = ({
	data,
	showSerialNumber = false,
}: RepairsDataGridProps) => {
	const {
		data: dialogData,
		open,
		handleOpen,
		handleClose,
	} = useDialogState<Id>();
	const { t } = useTranslation([
		'common',
		'commonButton',
		'productIndividual',
		'serviceProtocol',
	]);
	const queryClient = useQueryClient();

	const dialogSerialNumber =
		data.find((r) => r.id === dialogData?.id)?.serialNumber ?? '';

	const columns = useMemo((): GridColDef<RepairHistoryDto>[] => {
		const serialNumberColumn: GridColDef<RepairHistoryDto> = {
			field: 'serialNumber',
			headerName: t('common:serial-number'),
			minWidth: 128,
			flex: 1,
		};

		const baseColumns: GridColDef<RepairHistoryDto>[] = [
			{
				field: 'repairDate',
				headerName: t('productIndividual:claims-header-repair-date'),
				minWidth: 64,
				flex: 1,
				type: 'date',
				valueFormatter: (value) => getLocalizedDateFormat(value),
			},
			{
				field: 'mainOperationDescription',
				headerName: t('productIndividual:claim-details-main-operation'),
				minWidth: 256,
				flex: 3,
			},
			{
				field: 'mainPart',
				headerName: t('productIndividual:claim-details-causal-part'),
				minWidth: 64,
				flex: 1,
			},
			{
				field: 'engineRunningHours',
				headerName: t(
					'productIndividual:claims-header-operating-hours'
				),
				minWidth: 64,
				flex: 1,
				type: 'number',
				headerAlign: 'left',
				align: 'left',
				valueFormatter: (value) => toPrecision(value),
			},
			{
				field: 'performedBy',
				headerName: t('serviceProtocol:performed-by'),
				minWidth: 128,
				flex: 2,
			},
			{
				field: 'country',
				headerName: t('common:country'),
				minWidth: 60,
				flex: 1,
				renderCell: ({ row: { country } }) => (
					<CountryCell countryCode={country} />
				),
				align: 'left',
				display: 'flex',
			},
			{
				field: 'city',
				headerName: t('common:city'),
				minWidth: 128,
				flex: 2,
			},
			{
				field: 'actions',
				type: 'actions',
				minWidth: 128,
				flex: 2,
				getActions: ({ row }) => [
					<GridActionsCellItem
						key={`open-${row.id}`}
						icon={
							<Button
								component={Link}
								endIcon={<ArrowForwardOutlined />}
								to={getRepairViewLink(row.id)}
								variant="text"
								sx={{
									':hover': {
										background: 'transparent',
									},
								}}>
								{t('commonButton:open')}
							</Button>
						}
						label={t('commonButton:open')}
					/>,
					checkUserAccess(queryClient, {
						areaName: AreaName.Repair,
						permission: OperationType.Write,
					}) ? (
						<LinkActionItem
							href={getRepairEditLink(row.id)}
							key={`edit-${row.id}`}
							startIcon={<Edit />}
							label={t('commonButton:edit')}
							showInMenu
						/>
					) : (
						<></>
					),
					<LinkActionItem
						href={getRepairPrintLink(row.id, row.serialNumber)}
						key={`print-${row.id}`}
						startIcon={<PdfIcon />}
						label={t('commonButton:print')}
						target="_blank"
						showInMenu
					/>,
					<ProductIndividualRepairDeleteActionItem
						handleOpen={handleOpen}
						id={row.id}
						key={`delete-repair-${row.id}`}
						showInMenu
					/>,
				],
			},
		];

		return showSerialNumber
			? [serialNumberColumn, ...baseColumns]
			: baseColumns;
	}, [t, queryClient, handleOpen, showSerialNumber]);

	return (
		<>
			<VolvoDataGrid
				showToolbar
				initialState={{
					density: 'compact',
				}}
				rows={data}
				isLoading={false}
				columns={columns}
				slots={{ toolbar: DataGridToolbar }}
			/>
			<ProductIndividualRepairDeleteDialog
				data={dialogData}
				handleClose={handleClose}
				open={open}
				serialNumber={dialogSerialNumber}
			/>
		</>
	);
};
